import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { useRoute } from '@react-navigation/native';

import { useAsyncInit, useNavOptionsSet } from '@hooks';
import { IS_WEB, parseApiError } from '@utils';
import { ToastService } from '@services';
import { BaseText, ScreenLoader } from '@components';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import {
  MainNavigatorRoutePropType,
  MusicNavigatorRoutePropType,
  PersonalizationNavigatorRoutePropType,
} from '@navigation';
import { FollowersList } from './components/FollowersList';

const Container = styled(View)({
  flex: 1,
  bg: '$background0',
});

const ContentContainer = styled(View)({
  flex: 1,
  mx: [null, '5%'],
  maxWidth: [null, 600],
});

const Title = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xbig',
  fontWeight: 'semi',
});

const TotalCount = styled(Title)({
  color: '$text400',
});

type RoutePropType =
  | MusicNavigatorRoutePropType<'Followers'>
  | MainNavigatorRoutePropType<'Followers'>
  | PersonalizationNavigatorRoutePropType<'Followers'>;

export const FollowersScreen = observer(() => {
  const { params } = useRoute<RoutePropType>();
  const { t } = useTranslation();
  const viewer = useViewerModel();
  const item = viewer.getUserById(params.id);
  const totalCount = item?.followers.totalCount;

  useNavOptionsSet(
    {
      title: t('followers.title') + `: ${totalCount}`,
    },
    [totalCount],
  );

  useAsyncInit(async () => {
    try {
      await item?.followers.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }, [item]);

  async function refresh() {
    try {
      await item?.followers.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function fetchMore() {
    try {
      await item?.followers.fetchMore.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  if (!item) {
    return <ScreenLoader />;
  }

  return (
    <Container>
      <ContentContainer>
        {IS_WEB && (
          <Title>
            {t('followers.title')}{' '}
            <TotalCount>{totalCount}</TotalCount>
          </Title>
        )}

        <FollowersList
          list={item.followers}
          refresh={refresh}
          fetchMore={fetchMore}
        />
      </ContentContainer>
    </Container>
  );
});
